import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Avatar } from './Avatar';
import { api } from '../lib/api';

/** The server refuses above this too. Checked here so the message is instant. */
const MAX_BYTES = 2 * 1024 * 1024;

/**
 * Choosing a profile photo.
 *
 * ## The size limit is checked here as well as on the server
 *
 * Not because the client can be trusted — it cannot, and the API enforces the
 * same 2 MB independently — but because uploading four megabytes over a Kisii
 * connection only to be told no is a minute of somebody's life and their data
 * bundle. The check that protects the system is the one on the server; the
 * check that respects the person is this one.
 *
 * ## Why the version counter
 *
 * The photo is served from a stable URL, `/profile/avatar/:userId`, cached for
 * a day. That is right for every other screen and wrong for this one: after
 * uploading, the browser would keep showing the old picture and the upload
 * would look broken. Bumping a counter appended to the URL forces a re-fetch
 * of the one image that just changed, without weakening the caching that makes
 * faces cheap everywhere else.
 */
export function ProfilePhoto({ userId, name }: { userId: string | undefined; name: string }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const upload = useMutation({
    mutationFn: (data: string) => api.setAvatar(data),
    onSuccess: async () => {
      setError(null);
      setVersion((current) => current + 1);
      await queryClient.invalidateQueries({ queryKey: ['session'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.clearAvatar(),
    onSuccess: async () => {
      setError(null);
      setVersion((current) => current + 1);
      await queryClient.invalidateQueries({ queryKey: ['session'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const choose = (file: File) => {
    if (file.size > MAX_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      setError(
        `That photo is ${mb} MB and the limit is 2 MB. Most phones can save a smaller copy — or crop it square first, which is how it will be shown anyway.`,
      );
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => setError('That file could not be read.');
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') upload.mutate(result);
    };
    reader.readAsDataURL(file);
  };

  const busy = upload.isPending || remove.isPending;

  return (
    <div className="space-y-2 border-b border-border pb-4">
      <span className="block text-sm font-medium">Your photo</span>

      <div className="flex flex-wrap items-center gap-4">
        {/* `key` forces a fresh element, and with it a fresh request. */}
        <Avatar key={version} userId={userId} name={name || 'You'} size="xl" />

        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              {upload.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Camera className="h-4 w-4" aria-hidden="true" />
              )}
              Choose a photo
            </button>

            <button
              type="button"
              className="btn-quiet"
              disabled={busy}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              )}
              Remove it
            </button>
          </div>

          <p className="max-w-sm text-xs text-muted-foreground">
            A JPEG, PNG or WebP under 2 MB. It appears beside your name wherever you show up in
            the system — on tickets, on the roster, and on anything you comment on. Without one,
            your initials are used.
          </p>
        </div>
      </div>

      {/*
        Hidden and driven by the button, because a bare file input cannot be
        styled to match anything and reads as a foreign object on the page.
        `capture` is absent on purpose: on a phone this offers the camera *and*
        the gallery, and most people already have the photo they want.
      */}
      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) choose(file);
          // Cleared so choosing the same file twice still fires a change.
          event.target.value = '';
        }}
      />

      {error && (
        <p role="alert" className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </p>
      )}
    </div>
  );
}
